import React, { memo, useCallback } from 'react';
import { Card, CardBody, Col, Row } from 'reactstrap';
import { map } from 'rxjs/operators';
import { FormAjaxSelect, FormCheckbox, FormColorPicker, FormInput, FormSelect } from '@core/components/form';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { Button } from '@core/components/Button';
import { CommonService, ProposalService } from '@business/services';
import { GoogleAPI } from '@business/api/google_api';
import { GoogleFontLoader } from '@modules/common/GoogleFontLoader';
import { showDeleteConfirmation } from '@modules/common';
import { BlockTitleFormatForm, ProposalForm } from '../../ProposalForm';
import { newTitleFormatId, titleFormatStyle } from '@modules/proposal/Blocks/BlockContainer/titleFormat';

const escapeHtml = (text: string) =>
  text.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] as string));

const POSITION_ITEMS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];

const TitleFormatEditor = memo((props: { form: BlockTitleFormatForm; onRemove(): void }) => {
  const { form, onRemove } = props;
  const value = useNonNilObservable(form.value$);
  const preview = titleFormatStyle(value);

  const onSearch = useCallback((search: string) => GoogleAPI.fonts(search).pipe(map(fonts => CommonService.selectItems(fonts, 'family'))), []);

  return (
    <Card className="mb-3 shadow-none border">
      <CardBody>
        {/* Live preview — exactly how a section title using this format looks */}
        {!!value.font && <GoogleFontLoader font={value.font} />}
        <div className="mb-3 pb-3 border-bottom" style={{ textAlign: value.position || 'left' }}>
          <span style={preview.style}>{value.name || 'Untitled format'}</span>
        </div>
        <FormInput control={form.controls.name} />
        <Row>
          <Col md={6}>
            <FormAjaxSelect
              control={form.controls.font}
              callback={search => onSearch(search)}
              initalValue={value.font ? { label: value.font.family, value: value.font } : undefined}
            />
          </Col>
          <Col md={3}>
            <FormInput control={form.controls.fontSize} type="number" />
          </Col>
          <Col md={3}>
            <FormSelect control={form.controls.fontWeight} items={ProposalService.getAllFontWeight()} isClearable />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <FormColorPicker control={form.controls.color} />
          </Col>
          <Col md={6}>
            <FormSelect control={form.controls.position} items={POSITION_ITEMS} isClearable={false} />
          </Col>
        </Row>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex" style={{ gap: 24 }}>
            <FormCheckbox control={form.controls.italic} />
            <FormCheckbox control={form.controls.uppercase} />
          </div>
          <Button intent="danger" extraClassNames="btn-sm" onClick={onRemove}>
            <i className="bx bx-trash mr-1" /> Remove Format
          </Button>
        </div>
      </CardBody>
    </Card>
  );
});

/**
 * Block Settings tab → Block Title Formats: reusable section title styles saved on the template, listed as
 * cards. Admins add and remove formats here; a section picks one in its block settings ("Block Title
 * Format"). Changing a format restyles every section using it, and only that format.
 */
export const BlockTitleFormatSettings = memo((props: { form: ProposalForm }) => {
  const { form } = props;
  const formatsArray = form.controls.blockTitleFormats;
  const formatForms = useNonNilObservable(formatsArray.items$);
  const formats = useNonNilObservable(formatsArray.value$);

  const onAdd = useCallback(() => {
    const item = formatsArray.push();
    item.patchValue({ id: newTitleFormatId(), name: `Block Title ${formatsArray.items.length}` });
  }, [formatsArray]);

  const onRemove = useCallback(
    (index: number) => {
      const format = formats[index];
      if (!format) return;
      const usedBy = form.controls.blocks.items.filter(block => block.value.titleFormat === format.id);
      // The confirmation renders its message as HTML — escape the user-typed name
      const name = escapeHtml(format.name || 'Untitled format');
      const message = usedBy.length
        ? `${usedBy.length} section(s) use "${name}". They will go back to their own title styling.`
        : `Remove "${name}"?`;

      showDeleteConfirmation('Remove Block Title Format', message, { btnLabel: 'Remove' }).subscribe(() => {
        // Sections using it fall back to manual styling rather than pointing at a missing format
        usedBy.forEach(block => block.patchValue({ titleFormat: undefined }));
        formatsArray.removeAt(index);
      });
    },
    [form, formats, formatsArray],
  );

  return (
    <div>
      <div className="d-flex align-items-start justify-content-between mb-3">
        <div>
          <h5 className="font-size-15 mb-1">Block Title Formats</h5>
          <p className="text-muted font-size-12 mb-0">
            Pick one in a section&apos;s block settings (&quot;Block Title Format&quot;). Changing a format updates every section using it.
          </p>
        </div>
        <Button intent="primary" extraClassNames="btn-sm flex-shrink-0 ml-3" onClick={onAdd}>
          <i className="bx bx-plus mr-1" /> Add Format
        </Button>
      </div>
      {formatForms.length ? (
        formatForms.map((formatForm, index) => (
          <TitleFormatEditor key={formats[index]?.id || index} form={formatForm} onRemove={() => onRemove(index)} />
        ))
      ) : (
        <p className="text-muted font-size-13">No block title formats yet. Click &quot;Add Format&quot; to create one.</p>
      )}
    </div>
  );
});
